import type { BookingDTO, RescheduleBookingRequest } from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { toast } from '../../components/ui/Toast';
import { bookingsApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

export function useCoachBookings(): UseQueryResult<BookingDTO[]> {
  return useQuery({
    queryKey: queryKeys.bookings.all,
    queryFn: ({ signal }) => bookingsApi.listForCoach(signal),
  });
}

export function useCoachBookingsInRange(from: string, to: string): UseQueryResult<BookingDTO[]> {
  return useQuery({
    queryKey: queryKeys.bookings.range(from, to),
    queryFn: ({ signal }) => bookingsApi.listForCoachInRange(from, to, signal),
  });
}

export function useClientBookings(clientId: string): UseQueryResult<BookingDTO[]> {
  return useQuery({
    queryKey: queryKeys.bookings.byClient(clientId),
    queryFn: ({ signal }) => bookingsApi.listForClient(clientId, signal),
    enabled: clientId !== '',
  });
}

export function useCancelBooking(): UseMutationResult<BookingDTO, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookingsApi.cancel(id),
    onSuccess: (booking) => {
      void qc.invalidateQueries({ queryKey: queryKeys.bookings.all });
      void qc.invalidateQueries({ queryKey: queryKeys.bookings.byClient(booking.clientId) });
      toast.success('Booking cancelled');
    },
  });
}

export function useRescheduleBooking(): UseMutationResult<
  BookingDTO,
  unknown,
  { id: string; body: RescheduleBookingRequest }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => bookingsApi.reschedule(id, body),
    onSuccess: (booking) => {
      void qc.invalidateQueries({ queryKey: queryKeys.bookings.all });
      void qc.invalidateQueries({ queryKey: queryKeys.bookings.byClient(booking.clientId) });
      toast.success('Booking rescheduled');
    },
  });
}
